import { useState, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE;

/**
 * Хук для управления аутентификацией пользователя с интеграцией бэкенда
 */
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const [pendingEmail, setPendingEmail] = useState(null);
  const [registerStage, setRegisterStage] = useState(null); // null | 'email' | 'code' | 'password'
  const [registerEmail, setRegisterEmail] = useState(null);
  const [resetStage, setResetStage] = useState(null); // 'email' | 'code' | 'password' | null
  const [resetEmail, setResetEmail] = useState(null);

  const handleApiError = useCallback(async (response) => {
    if (!response.ok) {
      let errorMessage = 'Произошла ошибка';
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || `Ошибка ${response.status}`;
      } catch {
        errorMessage = `Ошибка ${response.status}: ${response.statusText}`;
      }

      switch (response.status) {
        case 400:
          errorMessage = errorMessage || 'Некорректные данные';
          break;
        case 401:
          errorMessage = errorMessage || 'Код подтверждения истёк или неверен';
          break;
        case 403:
          errorMessage = errorMessage || 'Неверный email или пароль';
          break;
        case 409:
          errorMessage = errorMessage || 'Email уже используется';
          break;
        case 500:
          errorMessage = errorMessage || 'Внутренняя ошибка сервера';
          break;
        default:
          break;
      }

      throw new Error(errorMessage);
    }
    return response;
  }, []);

  const login = useCallback(async (email, password) => {
    setIsLoading(true);
    setError(null);
    setNeedsConfirmation(false);
    setPendingEmail(null);

    try {
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
        credentials: 'include',
      });

      await handleApiError(response);

      // 200 — требуется подтверждение по email
      if (response.status === 200) {
        const data = await response.json();
        if (data.confirmation_sent) {
          setNeedsConfirmation(true);
          setPendingEmail(email);
          return { needsConfirmation: true, email };
        }
      }

      // 202 — успешная аутентификация
      if (response.status === 202) {
        // Токены устанавливаются через httpOnly cookies сервером
        // Перезагружаем страницу, чтобы выполнился useAuthInitialize и получили полные данные
        window.location.reload();
        return { needsConfirmation: false };
      }

      // Fallback для случаев, когда статус 200, но confirmation_sent не true
      const data = await response.json();
      if (data.token) {
        window.location.reload();
      }

      return { needsConfirmation: false };
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [handleApiError]);

  const confirmEmail = useCallback(async (code, pendingEmail) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/auth/confirm-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: pendingEmail,
          code,
        }),
        credentials: 'include',
      });

      await handleApiError(response);

      // После подтверждения email — успешная аутентификация
      // Перезагружаем страницу для выполнения useAuthInitialize
      setNeedsConfirmation(false);
      setPendingEmail(null);
      window.location.reload();

      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [handleApiError]);

  const startRegister = useCallback(async (email) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      await handleApiError(response);

      const data = await response.json();
      setRegisterStage('code');
      setRegisterEmail(email);
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [handleApiError]);

  const confirmRegisterCode = useCallback(async (code, email) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/auth/confirm-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email,
          code,
        }),
      });

      await handleApiError(response);

      setRegisterStage('password');
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [handleApiError]);

  const setRegisterPassword = useCallback(async (password) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/auth/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password }),
      });

      await handleApiError(response);

      // После успеха перезагружаем страницу
      setRegisterStage(null);
      setRegisterEmail(null);
      window.location.reload();
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [handleApiError]);

  const cancelRegister = useCallback(() => {
    setRegisterStage(null);
    setRegisterEmail(null);
    setError(null);
  }, []);

  const register = useCallback(async (email, password, confirmPassword) => {
    setIsLoading(true);
    setError(null);

    try {
      if (password !== confirmPassword) {
        throw new Error('Пароли не совпадают');
      }

      const response = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      await handleApiError(response);

      const data = await response.json();
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [handleApiError]);

  const logout = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (response.ok || response.status === 202) {
        setUser(null);
        setNeedsConfirmation(false);
        setPendingEmail(null);
        // Перезагружаем, чтобы очистить контекст
        window.location.reload();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshTokens = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        setUser(null);
        throw new Error('Сессия истекла');
      }

      return true;
    } catch (err) {
      setUser(null);
      throw err;
    }
  }, []);

  const cancelConfirmation = useCallback(() => {
    setNeedsConfirmation(false);
    setPendingEmail(null);
    setError(null);
  }, []);

  const resetPassword = useCallback(async (email) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
        credentials: 'include',
      });

      await handleApiError(response);

      setResetStage('code');
      setResetEmail(email);
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [handleApiError]);

  const confirmResetPassword = useCallback(async (code, email) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/auth/confirm-reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code }),
      });

      await handleApiError(response);

      setResetStage('password');
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [handleApiError]);

  const setNewPassword = useCallback(async (password) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/auth/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password }),
      });

      await handleApiError(response);

      // После успешной установки пароля
      setResetStage(null);
      setResetEmail(null);
      
      // Показываем уведомление или перезагружаем
      // window.location.reload();
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [handleApiError]);

  const startResetPasswordFlow = useCallback(() => {
    setResetStage('email');
    setResetEmail(null);
    setError(null);
  }, []);

  const cancelResetPassword = useCallback(() => {
    setResetStage(null);
    setResetEmail(null);
    setError(null);
  }, []);

  return {
    user,
    isLoading,
    error,
    login,
    confirmEmail,
    register,
    startRegister,
    confirmRegisterCode,
    setRegisterPassword,
    cancelRegister,
    logout,
    refreshTokens,
    isAuthenticated: !!user,
    needsConfirmation,
    pendingEmail,
    cancelConfirmation,
    registerStage,
    registerEmail,
    resetPassword,
    confirmResetPassword,
    setNewPassword,
    cancelResetPassword,
    resetStage,
    resetEmail,
    startResetPasswordFlow,
  };
};